import { useEffect, useState } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * How far back and forward this window can actually go, so the two arrows can be dimmed honestly.
 *
 * ## Why the position is read out of `history.state`
 *
 * `window.history` exposes a `length` and nothing else: it counts entries from before this
 * application was ever loaded, and says nothing about where in them we are. React Router's browser
 * history writes `{ usr, key, idx }` into `history.state` on every navigation, and `idx` is the
 * position in the session's entry list — the only legible answer available to a page.
 *
 * ⚠️ It can be absent, and that is a real state rather than a fault: a window restored by the
 * session plugin lands on an entry Router did not write, and a stray `pushState` from anything else
 * carries no `idx`. Treated as position zero, which dims `back` — the honest reading, because we
 * genuinely do not know that there is anything behind us.
 */
export interface IHistoryReach {
  /** Where we are now. */
  index: number;
  /** The furthest entry known to exist ahead of us — the high-water mark. */
  ceiling: number;
}

export function readHistoryIndex(state: unknown): number | null {
  if (!state || typeof state !== 'object') return null;
  const index = (state as { idx?: unknown }).idx;
  return typeof index === 'number' && Number.isInteger(index) && index >= 0 ? index : null;
}

/**
 * ⭐ **A push destroys every entry ahead of it, and the ceiling has to fall with them.**
 *
 * This is the whole of what makes `forward` truthful. Go back three pages and then click a link:
 * the three pages you could have gone forward to no longer exist, so a ceiling that only ever rose
 * would leave the arrow lit and pointing at nothing. A pop or a replace moves us within the entries
 * that are already there and leaves the ceiling where it was.
 */
export function nextHistoryReach(
  current: IHistoryReach,
  index: number,
  navigationType: 'POP' | 'PUSH' | 'REPLACE',
): IHistoryReach {
  if (navigationType === 'PUSH') return { index, ceiling: index };
  return { index, ceiling: Math.max(current.ceiling, index) };
}

export function historyReachOf(state: unknown): IHistoryReach {
  const index = readHistoryIndex(state) ?? 0;
  return { index, ceiling: index };
}

export function useHistoryReach(): { canGoBack: boolean; canGoForward: boolean } {
  const location = useLocation();
  const navigationType = useNavigationType();
  const [reach, setReach] = useState<IHistoryReach>(() =>
    historyReachOf(typeof window === 'undefined' ? null : window.history.state),
  );

  useEffect(() => {
    const index = readHistoryIndex(window.history.state) ?? 0;
    setReach((current) => nextHistoryReach(current, index, navigationType));
  }, [location.key, navigationType]);

  return { canGoBack: reach.index > 0, canGoForward: reach.index < reach.ceiling };
}
